import { useState, useRef, useCallback } from "react";
import { Mic, MicOff } from "lucide-react";
import { getLang } from "@/i18n";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export function SpeechToTextButton({ onTranscript, disabled, className = "" }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const toggle = useCallback(() => {
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser. Try Chrome or Safari.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = getLang() === "es" ? "es-US" : "en-US";
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript.trim()) onTranscript(transcript.trim());
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [listening, onTranscript]);

  if (!SpeechRecognition) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      data-testid="speech-to-text-btn"
      title={listening ? "Stop dictation" : "Start dictation"}
      className={`inline-flex items-center justify-center rounded-md transition-colors ${
        listening
          ? "bg-red-500 text-white animate-pulse shadow-md"
          : "bg-slate-100 text-slate-500 hover:bg-blue-100 hover:text-blue-600"
      } ${className}`}
    >
      {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}
